import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  VStack,
  Text,
  useToast,
  Progress,
  Badge,
  Flex,
  Container,
  useColorModeValue,
  Spinner,
  Fade,
  ScaleFade,
  Avatar,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../../config/firebase.ts';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBackIcon, TimeIcon, CheckCircleIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { useSound } from '../../contexts/SoundContext.tsx';
import PlayerName from '../Common/PlayerName.tsx';

// Minimal fetch helper (prefers explicit API url)
const API_BASE: string = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
async function api(path: string, init?: RequestInit) {
  const token = await auth.currentUser?.getIdToken?.();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers || {})
  } as any;
  
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
}

// Chakra + Framer Motion bridge
const MotionBox = motion(Box);

export default function SmartClashPage() {
  const toast = useToast();
  const params = useParams();
  const navigate = useNavigate();

  const [challenge, setChallenge] = useState<any | null>(null);
  const [answers, setAnswers] = useState<Record<number, { idx: number; t: number }>>({});
  const [playing, setPlaying] = useState(false);
  const [results, setResults] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState(15);
  const [inFeedback, setInFeedback] = useState(false);
  const [feedback, setFeedback] = useState<{ selectedIndex: number; correctIndex: number; isCorrect: boolean } | null>(null);
  const [shakeCard, setShakeCard] = useState(false);
  const [confetti, setConfetti] = useState<Array<{ id: number; x: number; delay: number; emoji: string }>>([]);
  const { playSound } = useSound();
  
  // Dark mode colors
  const bgColor = useColorModeValue('gray.100', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.300', 'gray.700');
  const winnerBg = useColorModeValue('purple.50', 'purple.700');
  const cardBgMuted = useColorModeValue('gray.50', 'gray.700');
  const pageGradient = useColorModeValue(
    'linear(to-b, rgba(255,255,255,0.02), rgba(128,90,213,0.06))',
    'linear(to-b, rgba(26,32,44,0.6), rgba(128,90,213,0.15))'
  );
  const progressTrackColor = useColorModeValue('gray.200', 'gray.600');

  // Fun animations
  const jelly = keyframes`
    0% { transform: scale(1); }
    30% { transform: scale(1.06, 0.94); }
    60% { transform: scale(0.97, 1.03); }
    100% { transform: scale(1); }
  `;
  const float = keyframes`
    0% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-6px) rotate(3deg); }
    100% { transform: translateY(0px) rotate(0deg); }
  `;
  const confettiFall = keyframes`
    0% { transform: translateY(-16px) rotate(0deg); opacity: 1; }
    100% { transform: translateY(220px) rotate(360deg); opacity: 0; }
  `;

  const triggerConfetti = () => {
    const ems = ['🎉','✨','🎊','💥','🌟','🍭','🍌','🧠'];
    const items = Array.from({ length: 24 }).map((_, i) => ({
      id: Date.now() + i,
      x: Math.floor(Math.random() * 100),
      delay: Math.random() * 0.4,
      emoji: ems[Math.floor(Math.random() * ems.length)]
    }));
    setConfetti(items);
    setTimeout(() => setConfetti([]), 1200);
  };

  // Mascot mood based on state
  const mascotEmoji = useMemo(() => {
    if (inFeedback) return feedback?.isCorrect ? '😎' : '🥴';
    return timeLeft <= 5 ? '😬' : '🤪';
  }, [inFeedback, feedback?.isCorrect, timeLeft]);

  // Timer effect
  useEffect(() => {
    let interval: any;
    if (playing && questionStartTime && timeLeft > 0 && !inFeedback) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - questionStartTime) / 1000);
        const remaining = Math.max(0, 15 - elapsed);
        setTimeLeft(remaining);
        
        if (remaining === 0) {
          // Auto submit on timeout
          const currentQuestion = challenge?.questions?.[currentQuestionIndex];
          if (currentQuestion) {
            handleAnswer(-1); // -1 for timeout
          }
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [playing, questionStartTime, timeLeft, currentQuestionIndex, challenge, inFeedback]);

  // Load challenge on mount if ID in URL
  useEffect(() => {
    const id = params.id;
    if (id) {
      loadChallenge(id);
    } else {
      setLoading(false);
    }
  }, [params.id]);

  const loadChallenge = async (challengeId: string) => {
    try {
      setLoading(true);
      const data = await api(`/api/smartclash/${challengeId}`, { method: 'GET' });
      setChallenge(data);
      
      // Check if already completed
      const resultData = await api(`/api/smartclash/${challengeId}/result`, { method: 'GET' });
      const me = auth.currentUser;
      const myUid = me?.uid;
      const myAttempt = (resultData?.attempts || []).find((a: any) => a.userId && myUid && a.userId === myUid);
      // Si je n'ai pas encore joué, démarrer la partie, sinon montrer les résultats
      if (!myAttempt) {
        setResults(null);
        setPlaying(true);
        setCurrentQuestionIndex(0);
        setQuestionStartTime(Date.now());
        setTimeLeft(15);
      } else {
        setResults(resultData);
      }
    } catch (e: any) {
      toast({ 
        title: 'Défi introuvable', 
        description: e.message, 
        status: 'error' 
      });
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Poll results if waiting for opponent (no winner yet and at least one attempt)
  useEffect(() => {
    let timer: any;
    const id = params.id;
    if (id && results && (!results.winner)) {
      timer = setInterval(async () => {
        try {
          const updated = await api(`/api/smartclash/${id}/result`, { method: 'GET' });
          setResults(updated);
        } catch (e) {
          // ignore
        }
      }, 3000);
    }
    return () => timer && clearInterval(timer);
  }, [params.id, results?.winner, results?.attempts?.length]);

  const handleAnswer = async (answerIndex: number) => {
    if (!challenge || !questionStartTime) return;

    const currentQuestion = challenge.questions[currentQuestionIndex];
    const timeMs = Date.now() - questionStartTime;
    
    // Store answer (answerIndex is 0-based, convert to 1-based for API)
    const selectedIndex = answerIndex >= 0 ? answerIndex + 1 : 0; // 0 for timeout
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: { idx: selectedIndex, t: timeMs }
    }));

    // Ask server to verify correctness for feedback
    try {
      setInFeedback(true);
      const id = challenge.id || params.id!;
      const verify = await api(`/api/smartclash/${id}/verify`, {
        method: 'POST',
        body: JSON.stringify({ questionId: currentQuestion.id, selectedIndex })
      });
      const fb = { selectedIndex, correctIndex: Number(verify.correctIndex || 0), isCorrect: !!verify.isCorrect };
      setFeedback(fb);
      // Play sound effect
      playSound(fb.isCorrect ? 'answerCorrect' : 'answerWrong');
      if (fb.isCorrect) {
        triggerConfetti();
      } else {
        setShakeCard(true);
      }
    } catch (e) {
      // If verify fails, still proceed without feedback
      setFeedback({ selectedIndex, correctIndex: 0, isCorrect: false });
      playSound('answerWrong');
      setShakeCard(true);
    }

    // Short delay (snappier)
    setTimeout(() => {
      setInFeedback(false);
      setFeedback(null);
  setShakeCard(false);
      if (currentQuestionIndex + 1 < challenge.questionCount) {
        setCurrentQuestionIndex((idx) => idx + 1);
        setQuestionStartTime(Date.now());
        setTimeLeft(15);
      } else {
        finishGame();
      }
    }, 600);
  };

  const finishGame = async () => {
    try {
      setPlaying(false);
      const answersArray = Object.entries(answers).map(([qid, v]) => ({
        questionId: Number(qid),
        selectedIndex: v.idx,
        timeMs: v.t,
      }));

      const me = auth.currentUser;
      const playerName = me?.displayName || 'Joueur';
      const id = (challenge as any)?.challengeId || (challenge as any)?.id || (params.id as string);

      const submitData = await api(`/api/smartclash/${id}/submit`, {
        method: 'POST',
        body: JSON.stringify({ answers: answersArray, playerName }),
      });
      const pts = Number((submitData as any).totalPoints) || 0;
      const good = Number((submitData as any).totalCorrect) || 0;
      toast({
        title: `🎯 Score: ${pts} points`,
        description: `${good}/${challenge?.questionCount || 0} bonnes réponses`,
        status: 'success',
        duration: 5000,
      });

      // Load results
      const resultData = await api(`/api/smartclash/${id}/result`, { method: 'GET' });
      setResults(resultData);
    } catch (e: any) {
      toast({
        title: 'Erreur',
        description: e.message,
        status: 'error',
      });
    }
  };

  const currentQuestion = challenge?.questions?.[currentQuestionIndex];
  const progress = challenge ? ((currentQuestionIndex + (playing ? 0 : 1)) / challenge.questionCount) * 100 : 0;

  return (
    <Box
      minH="100vh"
  bg={bgColor}
  bgGradient={pageGradient}
    >
      <Container maxW="4xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center">
            <HStack>
              <Button variant="ghost" onClick={() => navigate('/')} leftIcon={<ArrowBackIcon />}>Retour</Button>
              <Heading size="lg" color="purple.400">
                ⚔️ SmartClash
              </Heading>
            </HStack>
            {challenge && (
              <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                {challenge.categoryName || challenge.categoryKey}
              </Badge>
            )}
          </Flex>

          {/* Progress Bar */}
          {challenge && (
            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" color="gray.500">
                  Question {Math.min(currentQuestionIndex + 1, challenge.questionCount)} / {challenge.questionCount}
                </Text>
                <Text fontSize="sm" color="gray.500">Progression: {Math.round(progress)}%</Text>
              </Flex>
              <Progress value={progress} colorScheme="purple" borderRadius="md" bg={borderColor} size="lg" />
            </Box>
          )}

          {/* Game Area */}
          {playing && currentQuestion ? (
            <ScaleFade in={true} initialScale={0.95}>
              <MotionBox
                animate={shakeCard ? { x: [0, -8, 8, -6, 6, 0] } : { x: 0 }}
                transition={{ duration: 0.4 }}
                bg={cardBg}
                p={8}
                borderRadius="2xl"
                boxShadow="2xl"
                border="2px"
                borderColor={inFeedback ? (feedback?.isCorrect ? 'green.400' : 'red.400') : borderColor}
                position="relative"
                overflow="hidden"
              >
                {/* playful background stripes */}
                <Box
                  position="absolute"
                  inset={0}
                  pointerEvents="none"
                  opacity={0.06}
                  bgGradient="repeating-linear(135deg, rgba(128,90,213,0.8) 0px, rgba(128,90,213,0.8) 8px, transparent 8px, transparent 16px)"
                />
                {/* Confetti overlay */}
                <AnimatePresence>
                  {confetti.length > 0 && (
                    <Box position="absolute" inset={0} pointerEvents="none">
                      {confetti.map((c) => (
                        <Box
                          key={c.id}
                          position="absolute"
                          top={0}
                          left={`${c.x}%`}
                          fontSize="xl"
                          animation={`${confettiFall} 1.1s ease-in forwards`}
                          style={{ animationDelay: `${c.delay}s` }}
                        >
                          {c.emoji}
                        </Box>
                      ))}
                    </Box>
                  )}
                </AnimatePresence>

                {/* Timer + Mascot */}
                <Flex justify="center" mb={6} direction="column" align="center">
                  <Box fontSize="4xl" mb={2} animation={`${float} 2s ease-in-out infinite`}>
                    {mascotEmoji}
                  </Box>
                  <CircularProgress
                    value={(timeLeft / 15) * 100}
                    color={timeLeft <= 5 ? 'red.400' : 'purple.400'}
                    size="120px"
                    thickness="8px"
                    capIsRound
                    trackColor={progressTrackColor}
                    sx={{ animation: `${jelly} 0.9s ease ${inFeedback ? 'paused' : 'running'}` as any }}
                  >
                    <CircularProgressLabel fontSize="2xl" fontWeight="bold">{timeLeft}s</CircularProgressLabel>
                  </CircularProgress>
                </Flex>

                {/* Question */}
                <VStack spacing={6} align="stretch">
                  <Box textAlign="center">
                    <Text fontSize="xl" fontWeight="bold" mb={4}>
                      {currentQuestion.question}
                    </Text>
                  </Box>

                  {/* Answers */}
                  <VStack spacing={3}>
                    {currentQuestion.options.map((option: string, index: number) => {
                      // Determine visual state during feedback
                      const idx1 = index + 1;
                      const isSelected = feedback && feedback.selectedIndex === idx1;
                      const isCorrect = feedback && feedback.correctIndex === idx1;
                      const showGreen = inFeedback && isCorrect;
                      const showRed = inFeedback && isSelected && !isCorrect;
                      const variant = showGreen ? 'solid' : showRed ? 'solid' : 'outline';
                      const colorScheme = showGreen ? 'green' : showRed ? 'red' : 'purple';
                      return (
                        <Button
                          key={index}
                          as={motion.button}
                          whileHover={!inFeedback ? { scale: 1.02 } : undefined}
                          whileTap={!inFeedback ? { scale: 0.98 } : undefined}
                          onClick={() => !inFeedback && handleAnswer(index)}
                          size="lg"
                          variant={variant as any}
                          colorScheme={colorScheme as any}
                          justifyContent="flex-start"
                          p={6}
                          h="auto"
                          whiteSpace="normal"
                          textAlign="left"
                          w="full"
                          isDisabled={inFeedback}
                          _hover={!inFeedback ? {
                            bg: colorScheme === 'purple' ? 'purple.50' : undefined,
                            borderColor: colorScheme === 'purple' ? 'purple.400' : undefined,
                            transform: 'scale(1.01)'
                          } : {}}
                          transition="all 0.2s"
                        >
                          <HStack w="full">
                            <Badge colorScheme={colorScheme} mr={3}>
                              {String.fromCharCode(65 + index)}
                            </Badge>
                            <Text flex={1}>{option}</Text>
                          </HStack>
                        </Button>
                      );
                    })}
                  </VStack>
                </VStack>
              </MotionBox>
            </ScaleFade>
          ) : results ? (
            <Fade in={true}>
              <Box bg={cardBg} p={8} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
                <VStack spacing={6} align="stretch">
                  <Heading size="md" textAlign="center" color="purple.400">🏆 Résultats du défi</Heading>

                  {results.attempts && results.attempts.map((attempt: any) => {
                    const isWinner = results.winner && results.winner.attemptId === attempt.attemptId;
                    const me = auth.currentUser;
                    const isMe = !!(me && attempt.userId && me.uid === attempt.userId);
                    return (
                      <Box
                        key={attempt.attemptId}
                        p={6}
                        bg={isWinner ? winnerBg : cardBgMuted}
                        border="2px solid"
                        borderColor={isWinner ? 'purple.400' : borderColor}
                        borderRadius="lg"
                        position="relative"
                      >
                        {isWinner && (
                          <Badge colorScheme="yellow" position="absolute" top={-2} right={4} fontSize="sm">👑 Vainqueur</Badge>
                        )}

                        <Flex justify="space-between" align="center">
                          <HStack>
                            <Avatar size="sm" name={attempt.playerName} />
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="bold"><PlayerName userId={attempt.userId || ''} userName={attempt.playerName} checkEffect={!!attempt.userId} /> {isMe && '(Vous)'}</Text>
                              <HStack spacing={4}>
                                <HStack>
                                  <CheckCircleIcon color="green.400" />
                                  <Text fontSize="sm">{(attempt.totalCorrect ?? 0)}/{challenge?.questionCount}</Text>
                                </HStack>
                                <HStack>
                                  <TimeIcon color="blue.400" />
                                  <Text fontSize="sm">{Math.round((attempt.totalTimeMs ?? 0) / 1000)}s</Text>
                                </HStack>
                              </HStack>
                            </VStack>
                          </HStack>

                          <VStack align="end">
                            <Text fontSize="2xl" fontWeight="bold" color="purple.500">{attempt.totalPoints ?? 0}</Text>
                            <Text fontSize="sm" color="gray.500">points</Text>
                          </VStack>
                        </Flex>
                      </Box>
                    );
                  })}

                  {/* Waiting state if only one attempt and no winner */}
                  {results && (!results.winner) && (results.attempts?.length || 0) >= 1 && (
                    <VStack spacing={3} py={4}>
                      <Text color="gray.400">En attente de l'adversaire...</Text>
                      <Spinner size="md" color="purple.400" />
                    </VStack>
                  )}

                  <HStack justify="center" spacing={4} pt={4}>
                    <Button
                      colorScheme="blue"
                      leftIcon={<ExternalLinkIcon />}
                      onClick={async () => {
                        const url = window.location.href;
                        const title = 'Défi SmartClash';
                        try {
                          if ((navigator as any).share) {
                            await (navigator as any).share({ title, url });
                            return;
                          }
                        } catch {}
                        try {
                          await navigator.clipboard.writeText(url);
                          toast({
                            title: 'Lien copié',
                            description: "Envoie ce lien à ton adversaire pour qu'il joue !",
                            status: 'success',
                            duration: 4000,
                          });
                        } catch {
                          toast({ title: 'Impossible de copier le lien', status: 'error' });
                        }
                      }}
                    >
                      Partager le lien à ton adversaire
                    </Button>

                    <Button colorScheme="purple" onClick={() => navigate('/')}>Nouveau défi</Button>
                  </HStack>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Envoie le lien pour que l’adversaire rejoigne ce défi. Dès qu’il aura joué, le vainqueur sera affiché ici.
                  </Text>
                </VStack>
              </Box>
            </Fade>
          ) : (
            // État d'attente: ni playing ni results -> montrer un CTA pour démarrer si nécessaire
            <Fade in={true}>
              <Box bg={cardBg} p={8} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
                <VStack spacing={4}>
                  <Heading size="md" color="purple.400">⚔️ Défi prêt</Heading>
                  <Text color="gray.400" textAlign="center">Cliquez sur "Commencer" pour tenter ce défi.</Text>
                  <Button
                    colorScheme="purple"
                    onClick={() => {
                      setPlaying(true);
                      setCurrentQuestionIndex(0);
                      setQuestionStartTime(Date.now());
                      setTimeLeft(15);
                    }}
                  >
                    Commencer
                  </Button>
                </VStack>
              </Box>
            </Fade>
          )}
        </VStack>
      </Container>
    </Box>
  );
}
